import { Injectable } from '@angular/core';
import { AsyncSubject, BehaviorSubject, combineLatest } from 'rxjs';
import { HelpdeskEventStatus, HelpDeskTicket } from './help-desk-ticket';
import { MEAPIService } from 'src/app/common/services/api/me-api.service';
import { MEAPIResponse, MEDoNothingBasicResponseHandler, MEEntityAPIResponseHandler, ResponseCode } from 'src/app/common/services/api/me-api-response';
import { MEError } from 'src/app/common/error/me-error';
import { ChatService, ChatDialogState } from 'src/app/common/components/chat/chat.service';
import { ExternalSettingsService } from 'src/app/common/services/external-settings/external-settings.service';
import { hasOnePermission, InternalSettingsService } from 'src/app/common/services/internal-settings/internal-settings.service';
import { PermissionList } from 'src/app/common/services/internal-settings/internal-settings.interface';
import { PlatformService } from 'src/app/system/utility/platform.service';
import { HelpDeskDispatcherService } from 'src/app/common/services/message-handler/helpdesk-dispatcher.service';
import { ChatContextService } from 'src/app/common/components/chat/chat-context.service';

@Injectable({
  providedIn: 'root'
})
export class HelpDeskService {

  readonly API_LIST_TICKETS: string = MEAPIService.BASE_URL + '/customer/helpDeskTicket/list';
  readonly API_CREATE_HELPDESK: string = MEAPIService.BASE_URL + '/customer/helpDeskTicket/create';
  readonly API_CLOSE_HELPDESK: string = MEAPIService.BASE_URL + '/customer/helpDeskTicket/close';

  public tickets$: BehaviorSubject<HelpDeskTicket[]> = new BehaviorSubject<HelpDeskTicket[]>([]);

  constructor(
    private srvApi: MEAPIService,
    private srvChat: ChatService,
    private srvEs: ExternalSettingsService,
    private srvIs: InternalSettingsService,
    private srvChatContext: ChatContextService,
    private srvHelpdeskDispatcher: HelpDeskDispatcherService,
  ) {

    // Register to listen to tickets list changes
    this.tickets$.subscribe((tickets) => {
      if (tickets.length == 0) return;

      let isFound = false;
      for (let i = 0; i < tickets.length; i++) {
        let ticket = tickets[i];
        if (this.isInActiveState(ticket)) {
          this.srvChatContext.setChatTicket(ticket);
          this.srvChat.createChatDialog();
          isFound = true;
          break;
        }
      }

      if (!isFound) {
        this.srvChatContext.setChatTicket(null);
        this.srvChat.changeState(ChatDialogState.CLOSE);
      }
    });

    // Only return when user login, so when user refresh the screen, it won't be triggered.
    // Therefore, helpdesk.component.ts will try to reload if user go that page
    combineLatest([
      this.srvEs.externalSettings$,
      this.srvIs.internalSettings$,
    ])
      .subscribe(([es, is]) => {
        if (!es || !is) return;

        let isModuleEnabled = es.globalVariables.isModuleEnabledCustomerHelpDesk;
        let hasPermission = hasOnePermission([PermissionList.ACCESS_LIVE_CHAT], is.permissions);
        if (isModuleEnabled && hasPermission) {
          this.srvChat.loadCategories().subscribe(() => {
            this.loadTickets();
          });

          // Register Dispatcher for Mobile
          if (PlatformService.isMobile()) {
            this.srvHelpdeskDispatcher.registerDispatcher(this.srvChat.handleSocketMessage.bind(this.srvChat));
          }
        }
      });
  }

  public isInActiveState(ticket: HelpDeskTicket | null) {
    if (ticket == null) return false;

    let activeState = [HelpdeskEventStatus.UNKNOWN, HelpdeskEventStatus.QUEUE, HelpdeskEventStatus.ACTIVE];
    return (activeState.includes(ticket.status));
  }

  public loadTickets(): AsyncSubject<boolean> {
    return this.srvApi.Post<HelpDeskTicket[]>(this.API_LIST_TICKETS, {}, [new LoadTicketHandler(this.tickets$)]);
  }

  public createTicket(bean: {}): AsyncSubject<boolean> {
    let ret = new AsyncSubject<boolean>();
    this.srvApi.Post(this.API_CREATE_HELPDESK, bean, [
      new StartChat(this.srvChat, bean),
      new OutsideOfHours(this.srvChat, bean)
    ]).subscribe((isOkay: boolean) => {
      ret.next(isOkay);
      ret.complete();
    });
    return ret;

  }

  public closeTicket(isLogoff: boolean = false): AsyncSubject<HelpDeskTicket | null> {
    let ret = new AsyncSubject<HelpDeskTicket | null>();

    let ticket = this.srvChatContext.getChatTicket();
    if (!ticket) {
      ret.next(null);
      ret.complete();
      return ret;
    }

    this.srvApi.Post(this.API_CLOSE_HELPDESK, ticket, [
      new MEDoNothingBasicResponseHandler(ResponseCode.SUCCESS),
      new MEDoNothingBasicResponseHandler(ResponseCode.HD_TICKET_ALREADY_CLOSED), // Do not show error if it was already closed
    ]).subscribe((isOkay: boolean) => {
      if (isOkay) {
        if (!isLogoff) this.loadTickets();
        this.srvChat.changeState(ChatDialogState.CLOSE);
      }
      ret.next(ticket);
      ret.complete();
    });
    return ret;
  }
}

class LoadTicketHandler implements MEEntityAPIResponseHandler<HelpDeskTicket[]> {
  public code: ResponseCode = ResponseCode.SUCCESS;
  constructor(
    private tickets$: BehaviorSubject<HelpDeskTicket[]>
  ) { }

  handle(response: MEAPIResponse<HelpDeskTicket[]>): boolean | MEError {
    if (response.entity) {
      let tickets: HelpDeskTicket[] = response.entity;
      this.tickets$.next(tickets)
    }
    return true;
  }
}

class OutsideOfHours implements MEEntityAPIResponseHandler<HelpDeskTicket> {
  public code: ResponseCode = ResponseCode.HD_OUTSIDE_BUSINESS_HOURS;
  constructor(private srvChat: ChatService, private bean: any) { }

  handle(response: MEAPIResponse<HelpDeskTicket>): boolean | MEError {
    let businessHoursMessage = response.message;
    let userInputMessage = this.bean.message;
    let options = { userInputMessage, businessHoursMessage };
    this.srvChat.changeState(ChatDialogState.OUTSIDE_OF_HOUR, options);
    return true;
  }
}

class StartChat implements MEEntityAPIResponseHandler<HelpDeskTicket> {
  public code: ResponseCode = ResponseCode.SUCCESS;
  constructor(private srvChat: ChatService, private bean: any) { }

  handle(response: MEAPIResponse<HelpDeskTicket>): boolean | MEError {
    if (!response.entity) return true;
    let ticket = response.entity;
    let userInputMessage = this.bean.message;
    this.srvChat.changeState(ChatDialogState.ACTIVE, { ticket, userInputMessage });
    return true;
  }
}